// ============================================================================
// Ask AI's Projects card — what each action shows, and its save (item #944,
// the projects part; TCSD-0531 one tool per part, saved through the screen's
// own function; TCSD-0533 a card that moves money or cannot be undone reads
// it back). The server half is supabase/functions/am-copilot/projects.ts.
//
// Every save here is the function the project screen (project-detail.tsx)
// calls for the same button: addProjectNote, repeatProject, updateProjectStage,
// sendProjectBackToAm, resolveAwaitingAmInput, mintProjectServiceLinePayment,
// deleteProject. Draft → Ready to Invoice checks the screen's own blockers
// (lib/ready-gate.ts) on the project as it is when the card is pressed.
// ============================================================================

import {
  addProjectNote,
  deleteProject,
  fetchProject,
  mintProjectServiceLinePayment,
  repeatProject,
  resolveAwaitingAmInput,
  sendProjectBackToAm,
  updateProjectStage,
} from '../project-work/hooks/use-project-data';
import { savedProjectBlockers } from '../project-work/lib/ready-gate';
import type { ProjectStage } from '../../shared/types';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { moneyWords, projectButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const PROJECT_ACTIONS = [
  'add_note',
  'repeat_project',
  'move_stage',
  'send_back_to_am',
  'resubmit',
  'pay_line',
  'delete_project',
] as const;
export type ProjectAction = (typeof PROJECT_ACTIONS)[number];

/** A draft as the server persisted it (prepareProjectAction). */
export interface ProjectDraft {
  project_id: string;
  action: ProjectAction;
  fields: Record<string, unknown>;
  facility_id?: string | null;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface ProjectCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const blank = (v: unknown) => str(v).trim().length === 0;
const ctxOf = (d: ProjectDraft) => (d.context ?? {}) as Record<string, unknown>;

/** The project as people know it: "Building (cleaner) · what". */
export const projectOf = (d: ProjectDraft) => str(ctxOf(d).label) || d.project_id;

const MOVE_TITLES: Record<string, string> = {
  Ready_to_Invoice: 'Send to finance',
  Pending_PO: 'Pending PO',
  Invoiced: 'Mark Invoiced',
  Completed: 'Mark Completed',
};

/** What the card shows for this draft, with the person's edits `f`. */
export function projectCard(d: ProjectDraft, f: Record<string, unknown>): ProjectCardSpec {
  const c = ctxOf(d);
  const words = projectButtonWords(d.action, f, c);
  const stageLine = { label: 'Stage now', value: str(c.stage_words) || '—' };
  const base: ProjectCardSpec = { title: '', fields: [], info: [stageLine], readBack: null, doItLabel: words, missing: null };
  switch (d.action) {
    case 'add_note':
      return {
        ...base,
        title: 'Add a note',
        info: [],
        fields: [{ key: 'body', label: 'Note', kind: 'textarea', required: true }],
        missing: blank(f.body) ? 'Write the note.' : null,
      };
    case 'repeat_project':
      return {
        ...base,
        title: 'Repeat this one',
        info: [{ label: 'Repeats', value: projectOf(d) }],
        readBack: 'This makes a new Draft with the same building, work, sub and rates (no dates, no chargebacks). Nothing goes to finance until it is sent.',
      };
    case 'move_stage':
      return { ...base, title: MOVE_TITLES[str(f.to)] ?? 'Move the project' };
    case 'send_back_to_am':
      return {
        ...base,
        title: 'Send back to AM',
        fields: [{ key: 'reason', label: 'What finance needs from the AM', kind: 'textarea', required: true }],
        missing: blank(f.reason) ? 'Say what finance needs.' : null,
      };
    case 'resubmit':
      return { ...base, title: 'Mark addressed' };
    case 'pay_line': {
      const line = (c.line ?? {}) as Record<string, unknown>;
      const sub = str(line.provider_name) || 'the sub';
      return {
        ...base,
        title: 'Pay a project line',
        info: [
          { label: 'Line', value: str(line.label) || str(f.service_line_id) },
          { label: 'Sub', value: sub },
          { label: 'Amount', value: moneyWords(line.amount) },
        ],
        readBack: `This pays ${sub} ${moneyWords(line.amount)} for ${projectOf(d)}${line.pay_date ? ` (pay date ${str(line.pay_date)})` : ''}.${blank(line.note) ? '' : ` ${str(line.note)}`}`,
      };
    }
    case 'delete_project':
      return {
        ...base,
        title: 'Delete the project',
        fields: [{ key: 'reason', label: 'Why it was entered in error', kind: 'textarea', required: true }],
        readBack: `This deletes ${projectOf(d)}${c.paid === true ? ', and the sub payment already made from it' : ''}. Ask AI cannot undo it.`,
        missing: blank(f.reason) ? 'A reason is required to delete a project.' : null,
      };
  }
}

export interface ProjectCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): ProjectCommitResult => ({ ok: false, error });

/** Save one project draft through the project screen's own function. */
export async function commitProjectAction(
  d: ProjectDraft,
  f: Record<string, unknown>,
  eid: string | null,
): Promise<ProjectCommitResult> {
  const missing = projectCard(d, f).missing;
  if (missing) return fail(missing);
  if (!eid) return fail('Your sign-in has no employee record, so nothing can be saved in your name.');
  const pid = d.project_id;
  try {
    switch (d.action) {
      case 'add_note':
        await addProjectNote(pid, str(f.body).trim(), eid);
        return { ok: true, note: 'Note added.', target_table: 'project_notes', target_row_id: pid, committed_payload: { body: str(f.body).trim() } };
      case 'repeat_project': {
        const { project_id } = await repeatProject(pid, eid);
        return { ok: true, note: `New Draft ${project_id} — open it to check before sending.`, target_table: 'projects', target_row_id: project_id, committed_payload: { from: pid } };
      }
      case 'move_stage': {
        const to = str(f.to) as ProjectStage;
        if (to === 'Ready_to_Invoice') {
          // The screen's own gate, on the project as it is now.
          const live = await fetchProject(pid);
          if (!live) return fail('That project could not be read.');
          if (live.stage !== 'Draft') return fail(`This project is at ${live.stage} now, not Draft.`);
          const blockers = savedProjectBlockers(live);
          if (blockers.length) return fail(`Before this can go to finance: ${blockers.join(' · ')}.`);
        }
        await updateProjectStage(pid, to, eid);
        return { ok: true, note: `${projectOf(d)} — ${MOVE_TITLES[to] ?? to}.`, target_table: 'projects', target_row_id: pid, committed_payload: { stage: to } };
      }
      case 'send_back_to_am':
        await sendProjectBackToAm(pid, str(f.reason).trim(), eid);
        return { ok: true, note: 'Sent back to the AM.', target_table: 'projects', target_row_id: pid, committed_payload: { stage: 'Awaiting_AM_Input', reason: str(f.reason).trim() } };
      case 'resubmit':
        await resolveAwaitingAmInput(pid, eid);
        return { ok: true, note: 'Marked addressed — back to Ready to Invoice for finance.', target_table: 'projects', target_row_id: pid, committed_payload: { stage: 'Ready_to_Invoice' } };
      case 'pay_line': {
        const paymentId = await mintProjectServiceLinePayment(str(f.service_line_id), true);
        return { ok: true, note: `Paid — ${paymentId ?? 'payment made'}.`, target_table: 'partner_payments', target_row_id: paymentId, committed_payload: { service_line_id: str(f.service_line_id), payment_id: paymentId } };
      }
      case 'delete_project':
        await deleteProject(pid, str(f.reason));
        return { ok: true, note: 'Project deleted.', target_table: 'projects', target_row_id: pid, committed_payload: { reason: str(f.reason).trim() } };
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
